import React from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import { DetectionIcon } from '@vlabs/icons';
import { MultiSample } from '@vlabs/shared/components/sample/MultiSample';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { EventStreamLink } from '@vlabs/shared/components/stream-link/EventStreamLink';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { formatDateTime } from '@vlabs/shared/utils/date';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './EventHistoryCard.module.sass';

function EventHistoryCard({ event, isActive, onClick }): React.JSX.Element {
  const { t } = useTranslation();
  const matchType = event.top_match?.face_id ? 'face' : 'event';
  const location = buildLocationString(event);
  const createdAt = formatDateTime(event?.create_time);

  const bodyDetection = event?.body_detections?.find(({ sample_id }) => !!sample_id);
  const bodyDetectionUrl = getLunaSampleUrl('bodies', bodyDetection?.sample_id);

  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);
  const faceDetectionUrl = getLunaSampleUrl('faces', faceDetection?.sample_id);

  return (
    <div
      className={cn(st.Root, {
        [st.Root_active]: isActive,
      })}
    >
      <button
        aria-label="Event history"
        className={st.SelectCardButton}
        data-testid="workspace.eventHistoryCard.selectCardButton"
        onClick={() => onClick(event)}
        type="button"
      />

      <div className={st.Sample}>
        <MultiSample
          className={st.MultiSample}
          data-testid="workspace.eventHistoryCard.sample"
          primary={{
            imageUrl: faceDetectionUrl,
            type: 'face',
          }}
          secondary={{
            imageUrl: bodyDetectionUrl,
            type: 'body',
          }}
        />
      </div>

      <div className={st.EventInfo}>
        <div className={st.Main}>
          <div className={st.TextBlock}>
            <span data-testid="workspace.eventHistoryCard.createTime">
              {createdAt}
            </span>

            <div
              className={st.StreamLinkText}
              data-testid="workspace.eventHistoryCard.sources"
            >
              <EventStreamLink
                className={st.StreamLink}
                hasTruncatedLink
                hideId
                kind="secondary"
                placeholders={{
                  removed: t('lunapoint:event_card.videostream.removed'),
                  unavailable: t('lunapoint:event_card.videostream.unavailable'),
                }}
                size="medium"
                streamId={event.stream_id}
              />
            </div>
          </div>

          <div className={st.Tags}>
            <SimilarityTag
              data-testid="workspace.eventHistoryCard.similarityTagType"
              icon={<DetectionIcon />}
              size="medium"
              title={t(`lunapoint:person_card.${matchType === 'face' ? 'face' : 'event'}`)}
              value={event?.top_match?.similarity}
            />
          </div>
        </div>

        <div
          className={cn(st.Footer, {
            [st.EmptyLocationText]: location === t('lunapoint:no_address'),
          })}
          data-testid="workspace.eventHistoryCard.location"
        >
          <TruncatedText
            hasTruncation
            tooltipContent={location}
          >
            {location}
          </TruncatedText>
        </div>
      </div>
    </div>
  );
}

export { EventHistoryCard };
